import React, { useState, useEffect } from 'react'
import { Button, Form, Alert, Row, Col } from 'react-bootstrap'
import BigSpinner from 'common/components/BigSpinner'
import { Formik } from 'formik'
import { DataTable, ColumnDefinition } from 'common/components/DataTable'
import * as yup from 'yup'
import * as Icons from 'react-bootstrap-icons'
import Utils from 'common/Utils'
import API from 'common/logic/API'
import { Navigate, Link } from 'react-router-dom'
import { Fund, Tag } from 'types'
import TagSelect from 'common/components/TagSelect'

const ProcessState = Object.freeze({
    "LOADING": 1,
    "WAITING_FILE": 2,
    "UPLOADING": 3,
    "WAITING_LIST_REVIEW": 4,
    "EXECUTING": 5,
    "DONE": 6,
    "ERROR": 7
})

interface ParsedData {
    movements: Array<{
        description: string
        statementEntry: string
        date: string
        amount: string
        suggestedTagIds?: number[]
        notes?: string
    }>
    fund: Fund
    institution: string
}

interface MovementsReviewFormProps {
    parsed: ParsedData
    initialAvailableTags: Tag[]
    onReevaluate: () => void
    onExecuting: () => void
    onDone: () => void
    onError: (msg: string) => void
}

function MovementsReviewForm({
    parsed,
    initialAvailableTags,
    onReevaluate,
    onExecuting,
    onDone,
    onError,
}: MovementsReviewFormProps) {
    const [availableTags, setAvailableTags] = useState<Tag[]>(initialAvailableTags)
    const [tagIdsPerRow, setTagIdsPerRow] = useState<number[][]>(
        parsed.movements.map(m => m.suggestedTagIds ?? [])
    )

    const handleTagsChange = (rowIdx: number, ids: number[], updatedTags: Tag[]) => {
        setTagIdsPerRow(prev => {
            const copy = prev.slice()
            copy[rowIdx] = ids
            return copy
        })
        if (updatedTags.length !== availableTags.length) {
            setAvailableTags(updatedTags)
        }
    }

    const parseForm = (values: any) => {
        onExecuting()
        const payload = parsed.movements
            .map((movement: any, i: number) => ({
                description: movement.description,
                statementEntry: movement.statementEntry,
                fundId: parsed.fund.id,
                date: movement.date,
                accountableDate: movement.date,
                amount: movement.amount,
                tagIds: tagIdsPerRow[i] ?? [],
                notes: values.notes[i] || null
            }))
            .filter((_m: any, i: number) => values.import[i])

        API.post("/movements/batch", payload)
            .then(() => onDone())
            .catch((e: any) => onError(e.message))
    }

    const schema = yup.object().shape({
        import: yup.array(),
        notes: yup.array(),
    })

    const definitions = (handleChange: any, values: any) => [
        new ColumnDefinition("Import", (_obj: any, i: number) => (
            <Form.Check
                type="checkbox"
                label=""
                name={`import[${i}]`}
                checked={values.import[i]}
                onChange={handleChange}
            />
        )),
        new ColumnDefinition("Date", "date"),
        new ColumnDefinition("Description", "description"),
        new ColumnDefinition("Statement Entry", "statementEntry"),
        new ColumnDefinition("Amount", (obj: any) => Utils.formatMoney(obj.amount, true), "td-currency", "td-right"),
        new ColumnDefinition("Tags", (_obj: any, i: number) => (
            <div style={{ minWidth: '250px' }}>
                <TagSelect
                    selectedTagIds={tagIdsPerRow[i] ?? []}
                    availableTags={availableTags}
                    onChange={(ids, updatedTags) => handleTagsChange(i, ids, updatedTags)}
                />
            </div>
        )),
        new ColumnDefinition("Notes", (_obj: any, i: number) => (
            <Form.Control
                as='textarea'
                name={`notes[${i}]`}
                value={values.notes[i] || ""}
                onChange={handleChange}
                rows={1}
                style={{ minWidth: '150px' }}
            />
        )),
    ]

    const initialValues = {
        import: parsed.movements.map(() => true),
        notes: parsed.movements.map(() => ""),
    }

    return <>
        <Alert variant="success">Detected institution <strong>{parsed.institution}</strong> for fund <strong>{parsed.fund.name}</strong></Alert>
        You can add more classification rules in <a href="/classification-rules" target="_blank" rel="noreferrer">another tab</a> and then{' '}
        <button className="linkbutton" onClick={onReevaluate}>
            <Icons.ArrowClockwise /> Re-evaluate the statement
        </button>
        <Formik
            validationSchema={schema}
            onSubmit={parseForm}
            initialValues={initialValues}
        >
            {({ handleSubmit, handleChange, values }) => (
                <Form noValidate onSubmit={handleSubmit}>
                    <DataTable
                        columns={definitions(handleChange, values)}
                        rows={parsed.movements}
                    />
                    <Button variant="primary" type="submit">Insert</Button>
                </Form>
            )}
        </Formik>
    </>
}

function ImportStatement() {
    const [step, setStep] = useState<number>(ProcessState.LOADING)
    const [funds, setFunds] = useState<Fund[] | null>(null)
    const [tags, setTags] = useState<Tag[]>([])
    const [error, setError] = useState<React.ReactNode | null>(null)
    const [parsed, setParsed] = useState<ParsedData | null>(null)
    const [previouslySubmittedFileForm, setPreviouslySubmittedFileForm] = useState<any>(null)

    useEffect(() => {
        Promise.all([
            API.get('funds'),
            API.get('tags'),
        ]).then(([fundsResponse, tagsResponse]: any[]) => {
            setFunds(fundsResponse.data.results)
            setTags(tagsResponse.data.results)
            setStep(ProcessState.WAITING_FILE)
        }).catch((reason: any) => {
            setStep(ProcessState.ERROR)
            setError(reason.message)
        })
    }, [])

    const uploadAndParseFileForm = (values: any) => {
        setError(null)
        setPreviouslySubmittedFileForm(values)
        const fund = values.fund ? values.fund : ""
        setStep(ProcessState.UPLOADING)
        API.postFile(`/movements/statement?fund=${fund}`, values.file).then((response: any) => {
            setStep(ProcessState.WAITING_LIST_REVIEW)
            setParsed(response.data)
        }).catch((e: any) => {
            if (e.response && e.response.data.label === "missing-parameter") {
                setStep(ProcessState.WAITING_FILE)
                setError("Please specify the fund")
            } else if (e.response && e.response.data.label === "no-fund-with-institution") {
                const institution = e.response.data.stringFields.institution
                setStep(ProcessState.WAITING_FILE)
                setError(<>No fund is associated with institution <strong>{institution}</strong>. Please select a fund or <Link to="/funds">associate the institution with a fund</Link>.</>)
            } else {
                setStep(ProcessState.ERROR)
                setError(e.message)
            }
        })
    }

    const movementsForm = () => {
        if (!parsed || !parsed.movements || parsed.movements.length === 0) {
            return <Alert variant="warning">No new movements parsed</Alert>
        }
        return <MovementsReviewForm
            parsed={parsed}
            initialAvailableTags={tags}
            onReevaluate={() => uploadAndParseFileForm(previouslySubmittedFileForm)}
            onExecuting={() => setStep(ProcessState.EXECUTING)}
            onDone={() => setStep(ProcessState.DONE)}
            onError={(msg: string) => {
                setStep(ProcessState.ERROR)
                setError(msg)
            }}
        />
    }

    const fileForm = () => {
        const schema = yup.object().shape({
            file: yup.mixed().required(),
            fund: yup.number(),
        })

        const fundOptions = funds!
            .filter((f: Fund) => f.usedForNewMovements && !f.deleted)
            .map((item: Fund) => <option value={item.id} key={`funds_${item.id}`}>{item.name}</option>)

        return <>
            <Formik
                onSubmit={uploadAndParseFileForm}
                initialValues={{} as any}
                validationSchema={schema}
            >
                {({ handleSubmit, handleChange, values }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Form.Group as={Row} controlId="fileControl" className="mb-3">
                            <Form.Label column sm="2">Statement</Form.Label>
                            <Col>
                                <Form.Control type="file" size="sm" name="file"
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        (values as any).file = event.currentTarget.files![0]
                                    }}
                                />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} controlId="formFund">
                            <Form.Label column sm="2">Fund</Form.Label>
                            <Col>
                                <Form.Control
                                    as='select'
                                    name='fund'
                                    value={(values as any).fund}
                                    onChange={handleChange}
                                >
                                    <option>[Auto]</option>
                                    {fundOptions}
                                </Form.Control>
                            </Col>
                        </Form.Group>
                        <Button variant="primary" type="submit">Upload</Button>
                    </Form>
                )}
            </Formik>
        </>
    }

    const renderBody = () => {
        switch (step) {
            case ProcessState.LOADING:
                return <BigSpinner />
            case ProcessState.WAITING_FILE:
                return <>{fileForm()}</>
            case ProcessState.UPLOADING:
                return <BigSpinner />
            case ProcessState.WAITING_LIST_REVIEW:
                return <>{movementsForm()}</>
            case ProcessState.ERROR:
                return <></>
            case ProcessState.DONE:
                return <Navigate to="/movements" replace />
            default:
                return <></>
        }
    }

    return <>
        <h1>Import statement</h1>
        {error ? <Alert variant="danger">{error}</Alert> : null}
        {renderBody()}
    </>
}

export default ImportStatement
